import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { GraphLoader } from "pro-features/components/Loaders/src/ExpandedTableRowLoader/ExpandedTableRowLoader";
import { TrafficShareWithTooltip } from "pro-features/components/TrafficShare/src/TrafficShareWithTooltip";
import { i18nFilter } from "app/filters/i18nFilter";
import { CategoryLoyaltyTableContainer } from "app/pages/industry-analysis/category-loyalty/CategoryLoyaltyTableContainer";
import {
    GraphWapper,
    LegnedItem,
    LoyaltyTitleContainer,
    MaxLabel,
    MinLabel,
    TableBoxContainer,
} from "app/pages/industry-analysis/category-loyalty/StyledComponents";
import {
    GraphContainer,
    NoDataContainer,
    SitesChartLoaderContainer,
    StyledHeaderTitle,
} from "pro-features/pages/segments/components/benchmarkOvertime/StyledComponents";
import type { ICategoryLoyaltyTableData } from "app/pages/website-analysis/audience-loyalty/LoyaltyApiService";
import {
    BoxContainer,
    ContentContainer,
    LegendContainer,
    StyledBoxSubtitleLoyalty,
    StyledNoData,
} from "app/pages/website-analysis/audience-loyalty/StyledComponents";
import { NoSuggestionsArt } from "app/pages/workspace/common/arts/WorkspaceArts";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import DurationService from "app/services/DurationService";
import { LOYALTY_CATEGORIES_COLORS } from "./LoyaltyCategoriesColors";
import { proNavigator } from "single-spa/react-app/ProNavigator";

interface IIndustryAnalysisLoyalty {
    isCategoryChartLoading: boolean;
    categoryChartData: {
        backgroundColor: string;
        color: string;
        name: string;
        text: string;
        width: number;
    }[];
    isTableLoading: boolean;
    tableData: ICategoryLoyaltyTableData;
    pageFilters: any;
}

export const IndustryAnalysisLoyalty = (props: IIndustryAnalysisLoyalty) => {
    const { isCategoryChartLoading, categoryChartData, isTableLoading, tableData, pageFilters } = props;
    const noDataTexts = {
        title: "category.analysis.loyalty.no.data.title",
        subtitle: "category.analysis.loyalty.no.data.subtitle",
    };

    const renderLegend = () => {
        const legenditems = Object.keys(LOYALTY_CATEGORIES_COLORS).map((bucket) => {
            return (
                <LegnedItem
                    color={LOYALTY_CATEGORIES_COLORS[bucket].backgroundColor}
                    title={i18nFilter()(`industry.analysis.loyalty.legend.${bucket}`)}
                />
            );
        });
        return <LegendContainer>{legenditems}</LegendContainer>;
    };

    const params = proNavigator.getParams();
    const { from, to } = DurationService.getDurationData(params.duration).forAPI;
    const subtitleFilters = [
        {
            filter: "date",
            value: {
                from,
                to,
            },
        },
        {
            filter: "webSource",
            value: "Desktop", // values: available: 'Total' / 'Desktop'
        },
    ];

    const renderCategoryChart = () => {
        return (
            <BoxContainer data-automation-sites-vs-category={true}>
                <LoyaltyTitleContainer>
                    <FlexColumn>
                        <StyledHeaderTitle>
                            <BoxTitle tooltip={i18nFilter()("industry.analysis.loyalty.graph.title.tooltip")}>
                                {i18nFilter()("industry.analysis.loyalty.graph.title")}
                            </BoxTitle>
                        </StyledHeaderTitle>
                        <StyledBoxSubtitle>
                            <BoxSubtitle filters={subtitleFilters} />
                        </StyledBoxSubtitle>
                        <StyledBoxSubtitleLoyalty>
                            {i18nFilter()("industry.analysis.loyalty.graph.subtitle")}
                        </StyledBoxSubtitleLoyalty>
                    </FlexColumn>
                </LoyaltyTitleContainer>
                <ContentContainer>
                    {isCategoryChartLoading ? (
                        <SitesChartLoaderContainer>
                            <GraphLoader width={"100%"} />
                        </SitesChartLoaderContainer>
                    ) : (
                        <GraphContainer className={"sharedTooltip"}>
                            {categoryChartData && categoryChartData?.length > 0 ? (
                                <>
                                    {renderLegend()}
                                    <GraphWapper>
                                        <MinLabel>{i18nFilter()("industry.analysis.loyalty.legend.minLabel")}</MinLabel>
                                        <TrafficShareWithTooltip
                                            title={""}
                                            data={categoryChartData}
                                        />
                                        <MaxLabel>{i18nFilter()("industry.analysis.loyalty.legend.maxLabel")}</MaxLabel>
                                    </GraphWapper>
                                </>
                            ) : (
                                <NoDataContainer>
                                    {" "}
                                    <StyledNoData
                                        title={noDataTexts.title}
                                        subtitle={noDataTexts.subtitle}
                                        SvgImage={NoSuggestionsArt}
                                    />{" "}
                                </NoDataContainer>
                            )}
                        </GraphContainer>
                    )}
                </ContentContainer>
            </BoxContainer>
        );
    };

    const renderTable = () => {
        return (
            <TableBoxContainer data-automation-sites-vs-category={true}>
                <LoyaltyTitleContainer>
                    <FlexColumn>
                        <StyledHeaderTitle>
                            <BoxTitle tooltip={i18nFilter()("industry.analysis.loyalty.table.title.tooltip")}>
                                {i18nFilter()("audience.loyalty.table.title")}
                            </BoxTitle>
                        </StyledHeaderTitle>
                        <StyledBoxSubtitle>
                            <BoxSubtitle filters={subtitleFilters} />
                        </StyledBoxSubtitle>
                        <StyledBoxSubtitleLoyalty>
                            {i18nFilter()("industry.analysis.loyalty.table.subtitle")}
                        </StyledBoxSubtitleLoyalty>
                    </FlexColumn>
                </LoyaltyTitleContainer>
                <ContentContainer>
                    {!isTableLoading && !tableData?.Data ? (
                        <NoDataContainer>
                            {" "}
                            <StyledNoData
                                title={noDataTexts.title}
                                subtitle={noDataTexts.subtitle}
                                SvgImage={NoSuggestionsArt}
                            />{" "}
                        </NoDataContainer>
                    ) : (
                        <CategoryLoyaltyTableContainer
                            isLoading={isTableLoading}
                            tableData={tableData?.Data}
                            pageFilters={pageFilters}
                        />
                    )}
                </ContentContainer>
            </TableBoxContainer>
        );
    };

    return (
        <>
            {renderCategoryChart()}
            {renderTable()}
        </>
    );
};
